'use es6';

import { Set as ImmutableSet, List, Record } from 'immutable';
import Raven from 'raven-js'; // From https://developers.facebook.com/docs/marketing-api/targeting-specs#newplacement

const DevicePlatforms = {
  MOBILE: 'mobile',
  DESKTOP: 'desktop'
};
const PublisherPlatforms = {
  FACEBOOK: 'facebook',
  MESSENGER: 'messenger',
  INSTAGRAM: 'instagram',
  AUDIENCE_NETWORK: 'audience_network',
  ALL: 'all'
};
const Placements = {
  DEFAULT: 'DEFAULT',
  FACEBOOK_FEED: 'FACEBOOK_FEED',
  FACEBOOK_RIGHT_HAND_COLUMN: 'FACEBOOK_RIGHT_HAND_COLUMN',
  FACEBOOK_INSTANT_ARTICLE: 'FACEBOOK_INSTANT_ARTICLE',
  FACEBOOK_MARKETPLACE: 'FACEBOOK_MARKETPLACE',
  FACEBOOK_VIDEO_FEEDS: 'FACEBOOK_VIDEO_FEEDS',
  FACEBOOK_STORY: 'FACEBOOK_STORY',
  FACEBOOK_SEARCH: 'FACEBOOK_SEARCH',
  FACEBOOK_INSTREAM_VIDEO: 'FACEBOOK_INSTREAM_VIDEO',
  FACEBOOK_GROUPS: 'FACEBOOK_GROUPS',
  FACEBOOK_JOBS_BROWSER: 'FACEBOOK_JOBS_BROWSER',
  FACEBOOK_REELS_OVERLAY: 'FACEBOOK_REELS_OVERLAY',
  FACEBOOK_BUSINESS_DISCOVERY_FEED: 'FACEBOOK_BUSINESS_DISCOVERY_FEED',
  FACEBOOK_ALL: 'FACEBOOK_ALL',
  FACEBOOK_REELS: 'FACEBOOK_REELS',
  INSTAGRAM_SEARCH: 'INSTAGRAM_SEARCH',
  INSTAGRAM_STREAM: 'INSTAGRAM_STREAM',
  INSTAGRAM_STORY: 'INSTAGRAM_STORY',
  INSTAGRAM_EXPLORE: 'INSTAGRAM_EXPLORE',
  INSTAGRAM_EXPLORE_HOME: 'INSTAGRAM_EXPLORE_HOME',
  INSTAGRAM_PROFILE_FEED: 'INSTAGRAM_PROFILE_FEED',
  INSTAGRAM_REELS: 'INSTAGRAM_REELS',
  INSTAGRAM_REELS_OVERLAY: 'INSTAGRAM_REELS_OVERLAY',
  INSTAGRAM_SHOP: 'INSTAGRAM_SHOP',
  INSTAGRAM_IGTV: 'INSTAGRAM_IGTV',
  INSTAGRAM_ALL: 'INSTAGRAM_ALL',
  AUDIENCE_NETWORK_CLASSIC: 'AUDIENCE_NETWORK_CLASSIC',
  AUDIENCE_NETWORK_INSTREAM_VIDEO: 'AUDIENCE_NETWORK_INSTREAM_VIDEO',
  AUDIENCE_NETWORK_REWARDED_VIDEO: 'AUDIENCE_NETWORK_REWARDED_VIDEO',
  AUDIENCE_NETWORK_ALL: 'AUDIENCE_NETWORK_ALL',
  MESSENGER_HOME: 'MESSENGER_HOME',
  MESSENGER_SPONSORED_MESSAGES: 'MESSENGER_SPONSORED_MESSAGES',
  MESSENGER_STORY: 'MESSENGER_STORY'
};
const FacebookPositionsToPlacements = {
  feed: Placements.FACEBOOK_FEED,
  right_hand_column: Placements.FACEBOOK_RIGHT_HAND_COLUMN,
  instant_article: Placements.FACEBOOK_INSTANT_ARTICLE,
  instream_video: Placements.FACEBOOK_INSTREAM_VIDEO,
  marketplace: Placements.FACEBOOK_MARKETPLACE,
  video_feeds: Placements.FACEBOOK_VIDEO_FEEDS,
  story: Placements.FACEBOOK_STORY,
  search: Placements.FACEBOOK_SEARCH,
  facebook_groups_feed: Placements.FACEBOOK_GROUPS,
  groups_feed: Placements.FACEBOOK_GROUPS,
  jobs_browser: Placements.FACEBOOK_JOBS_BROWSER,
  reels_overlay: Placements.FACEBOOK_REELS_OVERLAY,
  facebook_reels: Placements.FACEBOOK_REELS,
  facebook_reels_overlay: Placements.FACEBOOK_REELS_OVERLAY,
  biz_disco_feed: Placements.FACEBOOK_BUSINESS_DISCOVERY_FEED,
  all: Placements.FACEBOOK_ALL
};
const InstagramPositionsToPlacements = {
  stream: Placements.INSTAGRAM_STREAM,
  story: Placements.INSTAGRAM_STORY,
  explore: Placements.INSTAGRAM_EXPLORE,
  explore_home: Placements.INSTAGRAM_EXPLORE_HOME,
  profile_feed: Placements.INSTAGRAM_PROFILE_FEED,
  reels: Placements.INSTAGRAM_REELS,
  reels_overlay: Placements.INSTAGRAM_REELS_OVERLAY,
  shop: Placements.INSTAGRAM_SHOP,
  igtv: Placements.INSTAGRAM_IGTV,
  ig_search: Placements.INSTAGRAM_SEARCH,
  all: Placements.INSTAGRAM_ALL
};
const AudienceNetworkPositionsToPlacements = {
  classic: Placements.AUDIENCE_NETWORK_CLASSIC,
  instream_video: Placements.AUDIENCE_NETWORK_INSTREAM_VIDEO,
  rewarded_video: Placements.AUDIENCE_NETWORK_REWARDED_VIDEO,
  all: Placements.AUDIENCE_NETWORK_ALL
};
const MessengerPositionsToPlacements = {
  messenger_home: Placements.MESSENGER_HOME,
  sponsored_messages: Placements.MESSENGER_SPONSORED_MESSAGES,
  story: Placements.MESSENGER_STORY
};

const getPlacementsOrSentry = (positions, PositionToPlacement) => positions.map(position => {
  if (PositionToPlacement[position]) {
    return PositionToPlacement[position];
  }

  Raven.captureMessage('Unknown Facebook placement', {
    extra: {
      position,
      PositionToPlacement
    }
  });
  return false;
}).filter(Boolean);

const DEFAULT_FACEBOOK_POSITIONS = ['all'];
const DEFAULT_INSTAGRAM_POSITIONS = ['all'];
const DEFAULT_AUDIENCE_NETWORK_POSITIONS = ['all'];
const DEFAULT_MESSENGER_POSITIONS = ['messenger_home', 'story'];
export default class FBPlacementRecord extends Record({
  devices: null,
  platforms: null,
  placements: null
}) {
  static fromRawTargeting(rawTargeting) {
    const devices = List(rawTargeting.device_platforms || [DevicePlatforms.MOBILE, DevicePlatforms.DESKTOP]);
    const platforms = List(rawTargeting.effective_publisher_platforms || rawTargeting.publisher_platforms || [PublisherPlatforms.ALL]);
    const facebook_positions = rawTargeting.effective_facebook_positions || rawTargeting.facebook_positions || (platforms.includes(PublisherPlatforms.FACEBOOK) ? DEFAULT_FACEBOOK_POSITIONS : []);
    const instagram_positions = rawTargeting.effective_instagram_positions || rawTargeting.instagram_positions || (platforms.includes(PublisherPlatforms.INSTAGRAM) ? DEFAULT_INSTAGRAM_POSITIONS : []);
    const audience_network_positions = rawTargeting.effective_audience_network_positions || rawTargeting.audience_network_positions || (platforms.includes(PublisherPlatforms.AUDIENCE_NETWORK) ? DEFAULT_AUDIENCE_NETWORK_POSITIONS : []);
    const messenger_positions = rawTargeting.effective_messenger_positions || rawTargeting.messenger_positions || (platforms.includes(PublisherPlatforms.MESSENGER) ? DEFAULT_MESSENGER_POSITIONS : []);
    const placements = ImmutableSet([...getPlacementsOrSentry(facebook_positions, FacebookPositionsToPlacements), ...getPlacementsOrSentry(instagram_positions, InstagramPositionsToPlacements), ...getPlacementsOrSentry(audience_network_positions, AudienceNetworkPositionsToPlacements), ...getPlacementsOrSentry(messenger_positions, MessengerPositionsToPlacements)]);
    return new FBPlacementRecord({
      devices,
      platforms,
      placements: placements.size ? placements : ImmutableSet([Placements.DEFAULT])
    });
  }

}